import { IPQSAnalysis, IpqsEmailDetail, IpqsPhoneDetail } from '@src/data/types/FraudAnalysisResult'
import { useQuery } from '@tanstack/react-query'
import { fraudApi } from './fraud-api'

const resolveEmailDetail = async (ipqsAnalysis: IPQSAnalysis): Promise<IpqsEmailDetail | null> => {
  if (ipqsAnalysis.ipqsEmailEntryId) {
    const result = await fraudApi.getIpqsEmailById(ipqsAnalysis.ipqsEmailEntryId)
    return result.response
  }
  if (ipqsAnalysis.emailRawResponse) {
    return JSON.parse(ipqsAnalysis.emailRawResponse) as IpqsEmailDetail
  }
  return defaultIpqsEmailDetail(ipqsAnalysis)
}
const defaultIpqsEmailDetail = (ipqsAnalysis?: IPQSAnalysis): IpqsEmailDetail | null => {
  if (!ipqsAnalysis) return null
  return {
    success: !ipqsAnalysis?.emailMessage,
    message: ipqsAnalysis?.emailMessage,
    fraudScore: ipqsAnalysis?.emailFraudScore,
    valid: ipqsAnalysis?.emailIsValid,
    disposable: ipqsAnalysis?.emailIsDisposable
  }
}

const resolvePhoneDetail = async (ipqsAnalysis: IPQSAnalysis): Promise<IpqsPhoneDetail | null> => {
  if (ipqsAnalysis.ipqsPhoneEntryId) {
    const result = await fraudApi.getIpqsPhoneById(ipqsAnalysis.ipqsPhoneEntryId)
    return result.response
  }
  if (ipqsAnalysis.phoneRawResponse) {
    return JSON.parse(ipqsAnalysis.phoneRawResponse) as IpqsPhoneDetail
  }
  return defaultIpqsPhoneDetail(ipqsAnalysis)
}
const defaultIpqsPhoneDetail = (ipqsAnalysis?: IPQSAnalysis): IpqsPhoneDetail | null => {
  if (!ipqsAnalysis) return null
  return {
    success: !ipqsAnalysis.phoneMessage,
    message: ipqsAnalysis.phoneMessage,
    fraudScore: ipqsAnalysis.phoneFraudScore,
    valid: ipqsAnalysis.phoneIsValid,
    active: ipqsAnalysis.phoneIsActive,
    voip: ipqsAnalysis.phoneIsVoip,
    region: ipqsAnalysis.phoneAssociatedRegion,
    name: ipqsAnalysis.phoneAssociatedName,
  }
}

interface IpqsDetails {
  emailDetail: IpqsEmailDetail | null
  phoneDetail: IpqsPhoneDetail | null
  loading: boolean
}

export const useIpqsDetails = (ipqsAnalysis?: IPQSAnalysis, creditApplicationId?: string): IpqsDetails => {
  const emailEntryId = ipqsAnalysis?.ipqsEmailEntryId
  const phoneEntryId = ipqsAnalysis?.ipqsPhoneEntryId

  const emailQuery = useQuery({
    queryKey: ['ipqs', 'email', emailEntryId ?? 'raw', ipqsAnalysis, creditApplicationId],
    queryFn: () => resolveEmailDetail(ipqsAnalysis!),
    enabled: !!ipqsAnalysis,
  })

  const phoneQuery = useQuery({
    queryKey: ['ipqs', 'phone', phoneEntryId ?? 'raw', ipqsAnalysis, creditApplicationId],
    queryFn: () => resolvePhoneDetail(ipqsAnalysis!),
    enabled: !!ipqsAnalysis,
  })

  return {
    emailDetail: emailQuery.data ?? defaultIpqsEmailDetail(ipqsAnalysis),
    phoneDetail: phoneQuery.data ?? defaultIpqsPhoneDetail(ipqsAnalysis),
    loading: emailQuery.isFetching || phoneQuery.isFetching,
  }
}
